import React, { createContext, useContext } from "react";
import * as Yup from "yup";
import { ErrorContext } from "@utils/errorMap";
import { IFormSchemaField } from "../types";

/**
 * Host-supplied configuration for the dynamic form engine. The renderer and its
 * field components are domain-agnostic; anything domain-specific — which
 * Currency fields suppress the currency symbol, how MaskedId values are
 * validated, where a user goes to change pre-filled data — is injected here by
 * the hosting surface (e.g. the employee-tax-forms routes) instead of being
 * hardcoded in the engine. Every value has a neutral default so the engine
 * renders standalone.
 */
export interface DynamicFormEngineConfig {
  /** Namespace segment of the localStorage draft key (`wsapp_<ns>_…`). */
  draftKeyNamespace: string;
  /** Country used when the form's metadata doesn't specify a jurisdiction. */
  defaultCountry: string;
  /** ISO 4217 code used to render Currency inputs and computed money values. */
  currencyCode: string;
  /** Catalog field keys typed `Currency` that render without a currency symbol. */
  fieldKeysWithoutCurrencySymbol: ReadonlySet<string>;
  /**
   * Domain validator layered onto MaskedId fields (e.g. an SSN/ITIN format
   * check). Absent → only the schema's own validation rules apply.
   */
  maskedIdValidator?: (
    field: IFormSchemaField,
    country: string
  ) => Yup.StringSchema;
  /**
   * Action for the pre-filled card's "Need to make a change?" link. The link is
   * hidden when absent.
   */
  onEditPrefilledData?: () => void;
  /**
   * `errorMap` context used to translate submit failures into user-facing copy
   * (ErrorAlert). Hosts with domain-specific error copy (e.g.
   * EmployeeTaxFormSubmit) override the generic default.
   */
  submitErrorContextKey: ErrorContext;
}

export const DEFAULT_DYNAMIC_FORM_ENGINE_CONFIG: DynamicFormEngineConfig = {
  draftKeyNamespace: "form",
  defaultCountry: "US",
  currencyCode: "USD",
  fieldKeysWithoutCurrencySymbol: new Set<string>(),
  submitErrorContextKey: "Other"
};

export const resolveDynamicFormEngineConfig = (
  config?: Partial<DynamicFormEngineConfig>
): DynamicFormEngineConfig => ({
  ...DEFAULT_DYNAMIC_FORM_ENGINE_CONFIG,
  ...config
});

const DynamicFormEngineConfigContext = createContext<DynamicFormEngineConfig>(
  DEFAULT_DYNAMIC_FORM_ENGINE_CONFIG
);

type ProviderProps = {
  config: DynamicFormEngineConfig;
  children: React.ReactNode;
};

export const DynamicFormEngineConfigProvider: React.FC<ProviderProps> = ({
  config,
  children
}) => (
  <DynamicFormEngineConfigContext.Provider value={config}>
    {children}
  </DynamicFormEngineConfigContext.Provider>
);

export const useDynamicFormEngineConfig = (): DynamicFormEngineConfig =>
  useContext(DynamicFormEngineConfigContext);
